<template>
    <div class="content mk-ref-list">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.referral_codes") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 12px;">
                        <h3 class="mb-0">{{ $t("marketing.menu.referral_codes") }}</h3>
                        <div class="d-flex flex-wrap" style="gap: 8px;">
                            <base-button type="secondary" size="sm" :loading="exporting" :disabled="schemaMissing" data-testid="rf-export" @click="exportCsv">
                                <i class="fa fa-file-csv"></i> {{ $t("marketing.referral.export_csv") }}
                            </base-button>
                            <template v-if="canWrite && !schemaMissing">
                                <base-button type="secondary" size="sm" data-testid="rf-bulk-open" @click="bulkOpen = true">
                                    <i class="fa fa-layer-group"></i> {{ $t("marketing.referral.bulk_create") }}
                                </base-button>
                                <base-button type="primary" size="sm" data-testid="rf-create" @click="openDialog(null)">
                                    <i class="fa fa-plus"></i> {{ $t("marketing.referral.create") }}
                                </base-button>
                            </template>
                        </div>
                    </div>
                </template>

                <div v-if="schemaMissing" class="px-4 pb-3">
                    <el-alert type="error" :closable="false" show-icon :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
                </div>

                <div class="px-4 pb-2">
                    <el-radio-group v-model="f.state" size="small" data-testid="rf-state-tabs" @change="reload(1)">
                        <el-radio-button label="">{{ $t("marketing.referral.tab_all") }}</el-radio-button>
                        <el-radio-button v-for="s in states" :key="s" :label="s">{{ $t("marketing.referral.state." + s) }}</el-radio-button>
                    </el-radio-group>
                </div>
                <div class="d-flex flex-wrap align-items-center px-4 pb-3" style="gap: 12px;">
                    <el-input v-model="f.q" size="small" clearable prefix-icon="el-icon-search" :placeholder="$t('marketing.referral.search')" style="max-width: 240px;" data-testid="rf-search" @input="onSearch"></el-input>
                    <el-select
                        v-model="f.owner_bo_user_id"
                        size="small"
                        filterable
                        remote
                        clearable
                        :remote-method="searchOwners"
                        :placeholder="$t('marketing.referral.filter_owner')"
                        style="width: 200px;"
                        @visible-change="(o) => o && !owners.length && searchOwners('')"
                        @change="reload(1)"
                    >
                        <el-option v-for="o in owners" :key="o.id" :value="o.id" :label="o.name"></el-option>
                    </el-select>
                    <el-select v-model="f.team" size="small" filterable clearable :placeholder="$t('marketing.referral.filter_team')" style="width: 170px;" @change="reload(1)">
                        <el-option v-for="t in teams" :key="t" :value="t" :label="t"></el-option>
                    </el-select>
                    <el-select v-model="f.campaign" size="small" filterable clearable :placeholder="$t('marketing.referral.filter_campaign')" style="width: 190px;" @change="reload(1)">
                        <el-option v-for="c in campaigns" :key="c" :value="c" :label="c"></el-option>
                    </el-select>
                    <el-tag v-if="f.batch_id" closable type="warning" data-testid="rf-batch-filter" @close="clearBatch">
                        {{ $t("marketing.referral.batch_filter") }} · {{ f.batch_id }}
                    </el-tag>
                </div>

                <el-table v-loading="loading" :data="rows" class="bon-table bon-tight" row-key="id" header-row-class-name="thead-light" :empty-text="$t('marketing.report.empty')" data-testid="rf-table">
                    <el-table-column :label="$t('marketing.referral.col.code')" width="150">
                        <template v-slot="{ row }">
                            <span class="rf-mono-text font-weight-bold">{{ row.code }}</span>
                            <a href="#" class="ml-1 text-muted" :aria-label="$t('marketing.referral.copy_link')" :title="$t('marketing.referral.copy_link')" @click.prevent="copy(row.link)"><i class="fa fa-copy"></i></a>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.name')" min-width="180">
                        <template v-slot="{ row }">{{ row.name || "-" }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.owner')" min-width="130">
                        <template v-slot="{ row }">{{ row.owner_name || $t("marketing.referral.none_label") }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.team')" min-width="110">
                        <template v-slot="{ row }">{{ row.team || "-" }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.campaign')" min-width="120">
                        <template v-slot="{ row }">{{ row.campaign || "-" }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.reward')" min-width="170">
                        <template v-slot="{ row }">
                            <span v-if="row.reward_type !== 'coupon'" class="text-muted">{{ $t("marketing.referral.no_reward") }}</span>
                            <template v-else>
                                <div class="small">{{ couponName(row) }}</div>
                                <el-tag v-if="isDead(row)" size="mini" type="danger" data-testid="rf-dead-tag">{{ $t("marketing.referral.hint.template_dead") }}</el-tag>
                            </template>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.used')" width="90" align="right">
                        <template v-slot="{ row }">{{ usage(row) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.window')" min-width="140">
                        <template v-slot="{ row }">
                            <span v-if="!row.starts_at && !row.ends_at" class="text-muted small">{{ $t("marketing.referral.always") }}</span>
                            <template v-else>
                                <div class="small text-nowrap">{{ row.starts_at ? fmt(row.starts_at) : "…" }} –</div>
                                <div class="small text-nowrap">{{ row.ends_at ? fmt(row.ends_at) : "…" }}</div>
                            </template>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.state')" width="110">
                        <template v-slot="{ row }">
                            <el-tag size="small" :type="stateTag(row.state)">{{ $t("marketing.referral.state." + row.state) }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.actions')" width="72" align="center" fixed="right">
                        <template v-slot="{ row }">
                            <el-dropdown trigger="click" @command="(c) => onAction(c, row)">
                                <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.referral.col.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item command="edit">{{ $t("marketing.referral.edit") }}</el-dropdown-item>
                                    <el-dropdown-item command="copy">{{ $t("marketing.referral.copy_link") }}</el-dropdown-item>
                                    <el-dropdown-item command="qr">{{ $t("marketing.referral.download_qr") }}</el-dropdown-item>
                                    <el-dropdown-item command="report">{{ $t("marketing.referral.view_report") }}</el-dropdown-item>
                                    <template v-if="canWrite">
                                        <el-dropdown-item v-if="row.status === 'disabled'" command="enable" divided>{{ $t("marketing.referral.enable") }}</el-dropdown-item>
                                        <el-dropdown-item v-else command="disable" divided class="text-danger">{{ $t("marketing.referral.disable") }}</el-dropdown-item>
                                    </template>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>

                <div slot="footer" class="d-flex justify-content-end px-3">
                    <el-pagination layout="total, prev, pager, next" :total="total" :page-size="limit" :current-page="f.page" @current-change="reload"></el-pagination>
                </div>
            </card>
        </div>

        <referral-code-dialog :visible.sync="dialogOpen" :code-id="dialogId" :can-write="canWrite" @saved="onSaved"></referral-code-dialog>
        <referral-bulk-dialog :visible.sync="bulkOpen" @created="onSaved" @view-batch="viewBatch"></referral-bulk-dialog>
    </div>
</template>

<script>
import { Table, TableColumn, Tag, Alert, RadioGroup, RadioButton, Dropdown, DropdownMenu, DropdownItem, Pagination, Select, Option, Input, Loading } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ReferralCodeService } from "@/resource";
import { apiErrorCode, fmtBkk, isSchemaMissing } from "@/utils/marketing";
import { copyToClipboard } from "@/utils/clipboard";
import { LIST_LIMIT, REFERRAL_STATES, buildCodeListQuery, referralStateTag, rewardDead, usageLabel } from "@/utils/referral";
import ReferralCodeDialog from "./components/ReferralCodeDialog.vue";
import ReferralBulkDialog from "./components/ReferralBulkDialog.vue";

// การตลาด → รหัสแนะนำ — list (Phase 2 spec 2026-10-13 §5.2).
export default {
    name: "MarketingReferralCodes",
    directives: { loading: Loading.directive },
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Pagination.name]: Pagination,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        ReferralCodeDialog,
        ReferralBulkDialog
    },
    data() {
        return {
            loading: false,
            exporting: false,
            schemaMissing: false,
            rows: [],
            total: 0,
            limit: LIST_LIMIT,
            states: REFERRAL_STATES,
            f: { page: 1, state: "", q: "", owner_bo_user_id: null, team: "", campaign: "", batch_id: "" },
            owners: [],
            teams: [],
            campaigns: [],
            dialogOpen: false,
            dialogId: null,
            bulkOpen: false,
            searchTimer: null,
            loadedOnce: false
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" })
    },
    created() {
        if (this.$route.query.batch_id) this.f.batch_id = String(this.$route.query.batch_id);
        this.reload(1);
        this.loadMeta();
    },
    // keep-alive (taskbar): refetch when the tab comes back, codes may have been used meanwhile.
    activated() {
        if (this.loadedOnce) this.reload();
    },
    methods: {
        fmt: fmtBkk,
        stateTag: referralStateTag,
        usage: usageLabel,
        isDead: rewardDead,
        couponName(row) {
            const c = row.reward_coupon;
            if (!c) return "#" + row.reward_coupon_template_id;
            return (this.$i18n.locale === "en" ? c.name_en : c.name_th) || c.name_th || c.code;
        },
        async reload(page) {
            if (page) this.f.page = page;
            this.loading = true;
            try {
                const res = await ReferralCodeService.list(buildCodeListQuery(this.f));
                const body = res.data || {};
                this.rows = body.data || [];
                this.total = body.total || 0;
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                this.total = 0;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.$notify({ type: "danger", message: this.$t("marketing.referral.err.load") });
            } finally {
                this.loading = false;
                this.loadedOnce = true;
            }
        },
        async loadMeta() {
            try {
                const res = await ReferralCodeService.meta();
                const d = (res.data && res.data.data) || {};
                this.teams = d.teams || [];
                this.campaigns = d.campaigns || [];
            } catch (e) {
                /* filters stay empty */
            }
        },
        async searchOwners(q) {
            try {
                const res = await ReferralCodeService.owners(q);
                this.owners = (res.data && res.data.data) || [];
            } catch (e) {
                /* keep */
            }
        },
        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(1), 350);
        },
        clearBatch() {
            this.f.batch_id = "";
            if (this.$route.query.batch_id) this.$router.replace({ query: { ...this.$route.query, batch_id: undefined } }).catch(() => {});
            this.reload(1);
        },
        viewBatch(batchId) {
            if (!batchId) return;
            this.f = { page: 1, state: "", q: "", owner_bo_user_id: null, team: "", campaign: "", batch_id: batchId };
            this.reload(1);
        },
        openDialog(id) {
            this.dialogId = id;
            this.dialogOpen = true;
        },
        onSaved() {
            this.reload();
            this.loadMeta();
        },
        async copy(link) {
            const ok = await copyToClipboard(link);
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.referral.copied") : this.$t("marketing.referral.err.generic") });
        },
        async downloadQr(row) {
            try {
                const res = await ReferralCodeService.qrBlob(row.id, { size: 1024, download: true });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `referral-${row.code}.png`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (err) {
                this.showError(err);
            }
        },
        async exportCsv() {
            this.exporting = true;
            try {
                const params = buildCodeListQuery(this.f);
                delete params.page;
                delete params.limit;
                const res = await ReferralCodeService.exportCsv(params);
                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "referral-codes.csv";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (err) {
                this.showError(err);
            } finally {
                this.exporting = false;
            }
        },
        showError(err) {
            const code = apiErrorCode(err);
            let msg = this.$t("marketing.referral.err.generic");
            if (isSchemaMissing(err)) msg = this.$t("marketing.schema_missing");
            else if (code && this.$te("marketing.referral.err." + code)) msg = this.$t("marketing.referral.err." + code);
            this.$notify({ type: "danger", message: msg });
        },
        async onAction(cmd, row) {
            switch (cmd) {
                case "edit":
                    this.openDialog(row.id);
                    return;
                case "copy":
                    this.copy(row.link);
                    return;
                case "qr":
                    this.downloadQr(row);
                    return;
                case "report":
                    this.$router.push({ path: "/marketing/reports/referral", query: { code_id: String(row.id) } });
                    return;
                case "disable": {
                    const r = await swal.fire({
                        text: this.$t("marketing.referral.confirm_disable", { code: row.code }),
                        icon: "warning",
                        showCancelButton: true,
                        reverseButtons: true,
                        focusCancel: true,
                        confirmButtonText: this.$t("marketing.referral.disable"),
                        cancelButtonText: this.$t("marketing.referral.cancel"),
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#6c757d"
                    });
                    if (!r.value) return;
                    break;
                }
                case "enable":
                    break;
                default:
                    return;
            }
            try {
                await ReferralCodeService[cmd](row.id);
                this.$notify({ type: "success", message: this.$t(cmd === "disable" ? "marketing.referral.disabled_ok" : "marketing.referral.enabled_ok") });
                this.reload();
            } catch (err) {
                this.showError(err);
            }
        }
    }
};
</script>

<style scoped>
.rf-mono-text {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.06em;
}
</style>
